import { costValue, summarizeCosts, priceDisplay, priceHistory, compareFuturesMonths } from './pricing-summary';
import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RmsModule } from '@rms';
import {
  RmsPulldownService,
  WebService,
  lastValueFrom,
  InventoryDataService, PopupService, RmsMessageService
} from '@services';
import { GroupFilterComponent } from '../group-filter/group-filter.component';
import { Column, TableIcon, FooterButton } from '@interfaces';
import { InventoryListComponent } from './inventory-list/inventory-list.component';
import { SkuPriceUpdateComponent } from './sku-price-update/sku-price-update.component';
import { SkuPriceHistoryComponent } from './sku-price-history/sku-price-history.component';

type NullableNumber = number | null;

interface InventoryDetailRow {
  AvailableQty: number;
  CoffeeSKUGroupID: number | null;
  DateIn: string | null;
  ForwardQty: number;
  LatestInventoryTypePriceDate: string | null;
  LatestInventoryTypePriceJson: any;
  InventoryTypePriceHistoryJson: any;
  InventoryTypeID: number;
  InventoryTypeName: string;
  InventoryTypePriceNeedsUpdate: boolean | number | null;
  
  agedwhseindif?: number | string | null;
  agedwhseincost?: number | string | null;
  AgedWhseInDif?: number | string | null;
  AgedWhseInCost?: number | string | null;
  AgedWhseInDiff?: number | string | null;


  /**
   * Used to decide whether a diff should be converted using NYC or LDN basis.
   * This should come from inventory_types.FuturesCompositeID.
   */
  FuturesCompositeID?: number | null;

  // hydrated from Groups/Countries/Regions
  GroupID?: number | null;
  GroupName?: string;
  CountryID?: number | null;
  CountryName?: string;
  RegionID?: number | null;
  RegionName?: string;
}

interface PricingRow {
  ID?: number;
  InventoryTypeID?: number;
  AskPriceDiff?: number | string | null;
  AskPriceFixed?: number | string | null;
  TraderID?: number;
  Comments?: string;
  PriceDate?: string;
  ReplacementFixed?: number | string | null;
  ReplacementDiffOld?: string | null;
  ReplacementDiff?: number | string | null;
  CurrentMarketPrice?: number | string | null;
  ForwardAskFixed?: number | string | null;
  ForwardAskDiff?: number | string | null;
  CurrentTariff?: number | string | null;

  /**
   * Optional fallback if this exists on the pricing JSON.
   * Primary source should still be InventoryDetailRow.FuturesCompositeID.
   */
  FuturesCompositeID?: number | null;
}

interface BasisRow {
  PriceBase?: number | string | null;
  FuturesCompositeID?: number | string | null;
  [key: string]: any;
}

interface PricedItem {
  item: InventoryDetailRow;
  price: PricingRow;
  source: 'latest' | 'history';
}

@Component({
  selector: 'app-sku-pricing-prep',
  standalone: true,
  imports: [CommonModule, RmsModule],
  templateUrl: './sku-pricing-prep.component.html',
  styleUrl: './sku-pricing-prep.component.scss'
})
export class SkuPricingPrepComponent implements OnInit, OnDestroy {
  @Input() DashboardInventoryTypeID?: number;
  @ViewChild('pricingTable') pricingTable?: { filteredData: any[] };
  get selectedCount() { return this.filteredSKUs.filter(row => row.Selected).length; }
  CustomGroups: any[] = [];
  Groups: any[] = [];
  Regions: any[] = [];
  Countries: any[] = [];

  filteredGroups: any[] = [];
  filteredCountries: any[] = [];

  InventoryDetails: InventoryDetailRow[] = [];
  filteredSKUs: any[] = [];

  RegionID: NullableNumber = null;
  CountryID: NullableNumber = null;
  GroupID: NullableNumber = null;

  CurrentNYCBasis: number | null = null;
  CurrentLDNBasis: number | null = null;

  CurrentNYCBasisRow: BasisRow | null = null;
  CurrentLDNBasisRow: BasisRow | null = null;

  SelectedGroup: any = null;
  SelectedGroups: any[] = [];
  SelectedCustomGroupID: number | null = null;
  SelectedCustomGroup: any = null;
  Summary: any = {};

  CustomGroupFilter: 'Single' | 'Custom' = 'Single';
  searchTerm = '';
  needsUpdateOnly = true;
  loading = false;
  loadError = '';
  FuturesMonth = '';
  futuresMonthOptions: any[] = [];
  private basisTimer?: ReturnType<typeof setInterval>;
  private refreshingBasis = false;
  private destroyed = false;
  ngOnDestroy() { this.destroyed = true; clearInterval(this.basisTimer); }
  private async refreshBasis() {
    if (this.refreshingBasis || this.loading || this.destroyed) return;
    this.refreshingBasis = true;
    try {
      const data = await this.invDataService.getData({
        keys: ['CurrentNYCBasis', 'CurrentLDNBasis'],
        turnOffLoader: true
      });
      if (this.destroyed) return;
      this.CurrentNYCBasisRow = data['CurrentNYCBasis']?.[0] ?? null;
      this.CurrentLDNBasisRow = data['CurrentLDNBasis']?.[0] ?? null;
      this.CurrentNYCBasis = this.toNumberOrNull(this.CurrentNYCBasisRow?.PriceBase);
      this.CurrentLDNBasis = this.toNumberOrNull(this.CurrentLDNBasisRow?.PriceBase);
      this.buildFilteredSKUs();
    if (!this.basisTimer && !this.destroyed) this.basisTimer = setInterval(() => void this.refreshBasis(), 60000);
    } catch { /* Keep the last successful basis until the next refresh. */ }
    finally { this.refreshingBasis = false; }
  }

  skuPricingColumns: Column[] = [];
  inventoryColumns: Column[] = [];

  InventoryToShow: any[] = [];

  customGroupOptions = [
    { value: 'Custom', label: 'Custom Group Filter' },
    { value: 'Single', label: 'Single Group Filter' }
  ];

  skuPricingRows: any[] = [];

  SkuIcons: TableIcon[] = [
    { icon: 'update', action: (row: any) => { this.openSKUUpdate(row); }, title: 'Update Price' },
    { icon: 'work_history', action: (row: any) => { this.openSKUHistory(row); }, title:'View History', }
  ]
  FooterButtons: FooterButton[] = [
    {label:'View Selected SKUs',onAction:()=>this.ShowSelectedSkuInventory(),class:'bg-blue',icon:'visibility'},
    {label: 'Update Selected SKUs', onAction: () => { this.openSKUUpdate(); }, class: 'bg-green', icon: 'update' }
  ]
  private groupBySkuGroupId = new Map<number, any>();
  private countryById = new Map<number, any>();
  private regionById = new Map<number, any>();
  private jsonParseCache = new Map<string, PricingRow[]>();

  constructor(
    private pulldownService: RmsPulldownService,
    private web: WebService,
    private invDataService: InventoryDataService,
    private popup: PopupService,
    private message: RmsMessageService
  ) { }

  async ngOnInit() {
    this.loading = true;
    this.loadError = '';
    try {
    await Promise.all([
      this.GetCustomFilters(),
      this.getGroupingData(),
      this.getInventoryDetails()
    ]);

    this.buildLookupMaps();
    this.hydrateInventoryRows();
    this.buildColumns();
    this.futuresMonthOptions = [...new Set(this.InventoryDetails.map((r: any) => r.FutureCompositeName).filter(Boolean))].sort(compareFuturesMonths).map(name => ({name}));
    this.buildFilteredSKUs();
    if (!this.basisTimer && !this.destroyed) this.basisTimer = setInterval(() => void this.refreshBasis(), 60000);
    } catch { this.loadError = 'Unable to load SKU pricing. Please retry.'; }
    finally { this.loading = false; }
  }

  async getGroupingData() {
    const data = await this.invDataService.getData({
      keys: ['Groups', 'Regions', 'Countries', 'CurrentNYCBasis', 'CurrentLDNBasis']
    });

    this.Groups = data?.['Groups'] || [];
    this.Regions = data?.['Regions'] || [];
    this.Countries = data?.['Countries'] || [];

    this.CurrentNYCBasisRow = data?.['CurrentNYCBasis']?.[0] || null;
    this.CurrentLDNBasisRow = data?.['CurrentLDNBasis']?.[0] || null;

    this.CurrentNYCBasis = this.toNumberOrNull(this.CurrentNYCBasisRow?.PriceBase);
    this.CurrentLDNBasis = this.toNumberOrNull(this.CurrentLDNBasisRow?.PriceBase);
  }

  async getInventoryDetails() {
    const inventory = await lastValueFrom(
      this.web.getWebCall('inventory', 'coffee/sku_pricing_inventory_details')
    );

    this.InventoryDetails = inventory || [];
  }

  async GetCustomFilters() {
    const params = [
      {
        name: 'UserID',
        value: localStorage.getItem('GUID') || ''
      }
    ];

    const filters = await lastValueFrom(
      this.web.getWebCall('users', 'custom-group-filters', { params })
    );

    this.CustomGroups = filters || [];
  }
  get SelectedRows(): boolean {
    return this.filteredSKUs.some((sku: any) => sku.Selected);
  }
  async openSKUUpdate(row?: any) {
    let selectedInventory: any[] = [];
    if (row) {
      selectedInventory.push({ ...row, pricing: { CurrentMarketPrice: this.getBasisForFuturesCompositeID(row.FuturesCompositeID) ?? this.CurrentNYCBasis } });
    }
    else {
      selectedInventory = this.filteredSKUs.filter((sku: any) => sku.Selected).map((sku: any) => { return { ...sku, pricing: { CurrentMarketPrice: this.getBasisForFuturesCompositeID(sku.FuturesCompositeID) ?? this.CurrentNYCBasis } }; });
    }
    if (!selectedInventory.length) return;
    const res = await this.pulldownService.open(SkuPriceUpdateComponent, {
      title: `Update SKU Pricing (${selectedInventory.length} SKUs)`,
      movable: true, width: '98vw', maxWidth: '1800px', center: false, panelClass: 'sku-pricing-top-pane',
      maxHeight: '100dvh',
      data: selectedInventory,
      top: '0',
    })
    let savedIndividualSku = false;
    const savedSubscription = res.returnData().subscribe((data: any) => {
      if (data?.success) savedIndividualSku = true;
    });
    res.afterClosed().subscribe(async (data: any) => {
      savedSubscription.unsubscribe();
      if (!data?.success && !savedIndividualSku) {
        return;
      }

      await this.ngOnInit();
      this.message.success('SKU pricing updated successfully.');
    })
  }
  openSKUHistory(row: any) {
    const history = priceHistory(row.InventoryTypePricingHistory)
      .map((h: any) => {
        return {
          ...h,
          AskPriceFixed: this.formatCurrencyDisplay(h.AskPriceFixed),
          AskPriceDiff: this.formatCPriceDisplay(h.AskPriceDiff),
          CurrentMarketPrice: this.formatCurrencyDisplay(h.CurrentMarketPrice),
          ReplacementFixed: this.formatCurrencyDisplay(h.ReplacementFixed),
          ReplacementDiff: this.formatCPriceDisplay(h.ReplacementDiff),
        }
      })
;
    this.popup.open({
      title: 'SKU Price History',
      data: { HistoricalPrices : history, CoffeeName: row.InventoryTypeName },
      component: SkuPriceHistoryComponent,
    });
  }
  private buildLookupMaps() {
    this.groupBySkuGroupId.clear();
    this.countryById.clear();
    this.regionById.clear();

    for (const region of this.Regions || []) {
      const regionId = Number(region.RegionID ?? region.RegionId ?? region.ID ?? region.Id);

      if (Number.isFinite(regionId)) {
        this.regionById.set(regionId, region);
      }
    }

    for (const country of this.Countries || []) {
      const countryId = Number(country.CountryID ?? country.CountryId ?? country.ID ?? country.Id);

      if (Number.isFinite(countryId)) {
        this.countryById.set(countryId, country);
      }
    }

    for (const group of this.Groups || []) {
      const coffeeSkuGroupId = Number(
        group.CoffeeSKUGroupID ??
        group.GroupID ??
        group.GroupId ??
        group.ID ??
        group.Id
      );

      if (Number.isFinite(coffeeSkuGroupId)) {
        this.groupBySkuGroupId.set(coffeeSkuGroupId, group);
      }
    }
  }

  private hydrateInventoryRows() {
    this.InventoryDetails = (this.InventoryDetails || []).map(row => {
      const coffeeSkuGroupId = Number(row.CoffeeSKUGroupID);

      const group = Number.isFinite(coffeeSkuGroupId)
        ? this.groupBySkuGroupId.get(coffeeSkuGroupId)
        : null;

      const countryId = Number(
        group?.CountryID ??
        group?.CountryId ??
        row.CountryID
      );

      const country = Number.isFinite(countryId)
        ? this.countryById.get(countryId)
        : null;

      const regionId = Number(
        country?.RegionID ??
        country?.RegionId ??
        group?.RegionID ??
        group?.RegionId ??
        row.RegionID
      );

      const region = Number.isFinite(regionId)
        ? this.regionById.get(regionId)
        : null;

      return {
        ...row,

        GroupID: Number.isFinite(coffeeSkuGroupId) ? coffeeSkuGroupId : null,
        GroupName:
          group?.GroupName ??
          group?.CoffeeSKUGroupName ??
          group?.Name ??
          row.GroupName ??
          '',

        CountryID: Number.isFinite(countryId) ? countryId : null,
        CountryName:
          country?.CountryName ??
          country?.Name ??
          row.CountryName ??
          '',

        RegionID: Number.isFinite(regionId) ? regionId : null,
        RegionName:
          region?.RegionName ??
          region?.Name ??
          row.RegionName ??
          ''
      };
    });
  }

  onChangeRegion() {
    this.CountryID = null;
    this.GroupID = null;

    this.filteredCountries = this.Countries.filter((c: any) =>
      Number(c.RegionID ?? c.RegionId) === Number(this.RegionID)
    );

    this.filteredGroups = [];
    this.buildFilteredSKUs();
  }

  onChangeCountry() {
    this.GroupID = null;

    this.filteredGroups = this.Groups.filter((g: any) =>
      Number(g.CountryID ?? g.CountryId) === Number(this.CountryID)
    );

    this.buildFilteredSKUs();
  }

  onChangeGroup() {
    this.SelectedGroup = this.Groups.find(group => group.GroupID === this.GroupID )
    this.buildFilteredSKUs();
  }

  onChangeCustomGroupFilter() {
    this.RegionID = null;
    this.CountryID = null;
    this.GroupID = null;

    this.SelectedCustomGroupID = null;
    this.SelectedCustomGroup = null;
    this.SelectedGroups = [];

    this.DashboardInventoryTypeID = undefined;
    this.searchTerm = '';
    this.FuturesMonth = '';
    this.SelectedGroup = null;
    this.filteredCountries = [];
    this.filteredGroups = [];

    this.SelectedCustomGroup = this.CustomGroups.find(group => group.CustomGroupFilterID === this.SelectedCustomGroupID);

    this.buildFilteredSKUs();
  }

  onChangeCustomGroup() {
    this.SelectedCustomGroup = this.CustomGroups.find(group =>
      Number(group.CustomGroupFilterID) === Number(this.SelectedCustomGroupID)
    );

    if (!this.SelectedCustomGroup) {
      this.SelectedGroups = [];
      this.buildFilteredSKUs();
      return;
    }

    let groups: any[] = [];
    try { groups = JSON.parse(this.SelectedCustomGroup.CustomGroupFilter || '[]'); } catch { this.message.error('This saved group has invalid filter data. Please edit it.'); }
    if (!Array.isArray(groups)) groups = [];

    this.SelectedGroups = groups
      .map((g: any) => Number(g.GroupID ?? g.CoffeeSKUGroupID ?? g.ID ?? g.Id))
      .filter((x: number) => Number.isFinite(x));

    this.buildFilteredSKUs();
  }

  onSearchChange() {
    this.buildFilteredSKUs();
  }

  async ViewCustomFilters() {
    const ref = this.pulldownService.open(GroupFilterComponent, {
      data: { customGroups: this.CustomGroups || [] },
      title: 'Custom Group Filters',
      width: '96vw',
      top: '0',
      maxHeight: '100vh',
      minHeight: '30vh'
    });

    ref.returnData().subscribe(async (data: any) => {
      if (data?.action === 'refresh') {
        await this.GetCustomFilters();

        ref.updateData({
          customGroups: this.CustomGroups
        });
      }
    });

    ref.afterClosed().subscribe(async () => {
      await this.GetCustomFilters();
      if (this.SelectedCustomGroupID) this.onChangeCustomGroup();
    });
  }

  ClearSearch() {
    this.RegionID = null;
    this.CountryID = null;
    this.GroupID = null;

    this.SelectedCustomGroupID = null;
    this.SelectedCustomGroup = null;
    this.SelectedGroups = [];

    this.searchTerm = '';
    this.FuturesMonth = '';
    this.SelectedGroup = null;
    this.filteredCountries = [];
    this.filteredGroups = [];

    this.buildFilteredSKUs();
  }

  buildFilteredSKUs() {
    const selected = new Set(this.filteredSKUs.filter(s => s.Selected).map(s => s.InventoryTypeID));
    const rows = this.applyInventoryFilters(this.InventoryDetails || []);
    const skuGrouped = new Map<string, InventoryDetailRow[]>();

    for (const row of rows) {
      const key = this.getInventoryTypeKey(row);

      if (!key) {
        continue;
      }

      const existing = skuGrouped.get(key);

      if (existing) {
        existing.push(row);
      } else {
        skuGrouped.set(key, [row]);
      }
    }

    this.filteredSKUs = Array.from(skuGrouped.entries()).map(([key, inventory]) => {
      const pricedItems = this.getPricedItems(inventory);

      const latestPricedItems = pricedItems.filter(x => x.source === 'latest');

      const latestPrices = priceHistory(latestPricedItems.map(x => x.price));

      const latestAskFixed = this.firstPriceNumber(latestPrices, 'AskPriceFixed');
      const latestAskDiff = this.firstPriceNumber(latestPrices, 'AskPriceDiff');

      const latestReplacementFixed = this.firstPriceNumber(latestPrices, 'ReplacementFixed');
      const latestReplacementDiff = this.firstPriceNumber(latestPrices, 'ReplacementDiff');

      const latestMarketPrice = this.firstPriceNumber(latestPrices, 'CurrentMarketPrice');

      const latestForwardAskFixed = this.firstPriceNumber(latestPrices, 'ForwardAskFixed');
      const latestForwardAskDiff = this.firstPriceNumber(latestPrices, 'ForwardAskDiff');

      /**
       * Keep these only if you still want min/max/all values elsewhere.
       */
      const latestAskFixedValues = this.getNumberValues(latestPrices, 'AskPriceFixed');
      const latestReplacementFixedValues = this.getNumberValues(latestPrices, 'ReplacementFixed');

      /**
       * Inventory-based averages.
       *
       * Spot:
       *   fixed = avg(agedwhseincost) where AvailableQty > 0
       *   diff  = avg(agedwhseindif) where AvailableQty > 0
       *
       * Forward:
       *   fixed = avg(agedwhseincost) where ForwardQty > 0
       *   diff  = avg(agedwhseindif) where ForwardQty > 0
       */
      const spotInventoryRows = inventory.filter(x =>
        this.numberOrZero(x.AvailableQty) > 0
      );

      const forwardInventoryRows = inventory.filter(x =>
        this.numberOrZero(x.ForwardQty) > 0
      );

      const spot = summarizeCosts(inventory, 'AvailableQty', 'aged');
      const forward = summarizeCosts(inventory, 'ForwardQty', 'landed');
      const spotFixedAvg = spot.fixed.average, spotDiffAvg = spot.diff.average;
      const forwardFixedAvg = forward.fixed.average, forwardDiffAvg = forward.diff.average;

      /**
       * RNY Overall Fixed/Diff averages come from ALL inventory aged values.
       *
       * Fixed:
       *   avg(agedwhseincost) across all inventory rows where fixed > 0
       *
       * Diff:
       *   avg(agedwhseindif) across all inventory rows only when that row
       *   does NOT have a fixed aged cost.
       */
      const overallInventoryFixedValues = inventory
        .map(x => this.getUsableAgedFixed(x))
        .filter((v): v is number => v !== null);

      const overallInventoryDiffValues = inventory
        .map(x => this.getUsableAgedDiff(x))
        .filter((v): v is number => v !== null);

      const overallInventoryFixedAvg = this.average(overallInventoryFixedValues);
      const overallInventoryDiffAvg = this.average(overallInventoryDiffValues);

      const suggestedFixedPrice = this.getSuggestedPrice(
        overallInventoryFixedAvg,
        'Fixed'
      );

      const suggestedDiffPrice = this.getSuggestedPrice(
        overallInventoryDiffAvg,
        'Diff',
        this.CurrentNYCBasis
      );
      const suggestedPriceDisplay =
        this.buildSlashPriceDisplay(
          suggestedDiffPrice,
          suggestedFixedPrice
        );
      /**
       * Worst-case comparable values:
       *
       * Overall values still come from latest pricing JSON.
       * Replace values now come from aged inventory row values.
       */
      /**
       * Worst-case comparison should use the displayed values:
       *
       * RNY Overall Fixed Avg = overall aged fixed avg
       * RNY Overall Diff Avg  = overall aged diff avg converted to comparable fixed value
       * Replace Fixed         = latest known replacement fixed
       * Replace Diff          = latest known replacement diff converted to comparable fixed value
       */
      const sample = inventory[0];
      const overallInventoryDiffComparableValues = inventory
        .map(x =>
          this.diffToComparableValue(
            this.getUsableAgedDiff(x),
            x
          )
        )
        .filter((v): v is number => v !== null);

      const overallFixedComparableAvg = overallInventoryFixedAvg;

      const overallDiffComparableAvg = this.average(overallInventoryDiffComparableValues);

      const replaceFixedComparableAvg = latestReplacementFixed;

      const replaceDiffComparableAvg = this.diffToComparableValue(
        latestReplacementDiff,
        sample
      );
      const worstCase = this.getWorstCaseColumn([
        {
          key: 'AverageLatestReplacementFixed',
          comparableValue: overallFixedComparableAvg
        },
        {
          key: 'AverageLatestReplacementDiff',
          comparableValue:overallDiffComparableAvg
        },
        {
          key: 'AverageHistoricalReplacementFixed',
          comparableValue: replaceFixedComparableAvg
        },
        {
          key: 'AverageHistoricalReplacementDiff',
          comparableValue: replaceDiffComparableAvg
        }
      ]);



      const totalAvailableQty = inventory.reduce(
        (sum, x) => sum + this.numberOrZero(x.AvailableQty),
        0
      );

      const totalForwardQty = inventory.reduce(
        (sum, x) => sum + this.numberOrZero(x.ForwardQty),
        0
      );

      const needsUpdate = inventory.some(x =>
        x.InventoryTypePriceNeedsUpdate === true ||
        x.InventoryTypePriceNeedsUpdate === 1
      );

      const groupName = sample.GroupName ?? '';
      const inventoryTypeName = sample.InventoryTypeName ?? '';

      return {
        SKU: key,
        Selected: selected.has(sample.InventoryTypeID),
        FuturesCompositeID: sample.FuturesCompositeID,
        SpotCostDetail: spot.detail,
        ForwardCostDetail: forward.detail,
        LatestSavedPricing: latestPrices[0] ?? {},

        InventoryTypeID: sample.InventoryTypeID ?? null,
        InventoryTypeName: inventoryTypeName,
        InventoryTypePricingHistory: priceHistory(sample.InventoryTypePriceHistoryJson),
        CoffeeSKUGroupID: sample.CoffeeSKUGroupID ?? null,
        GroupID: sample.GroupID ?? sample.CoffeeSKUGroupID ?? null,
        GroupName: groupName,

        GroupSortName: `${groupName} ${inventoryTypeName}`.trim(),

        CountryID: sample.CountryID ?? null,
        CountryName: sample.CountryName ?? '',

        RegionID: sample.RegionID ?? null,
        RegionName: sample.RegionName ?? '',

        InventoryCount: inventory.length,

        TotalAvailableQty: Number(totalAvailableQty.toFixed(4)),
        TotalForwardQty: Number(totalForwardQty.toFixed(4)),

        Inventory: inventory,

        LatestInventoryTypePrices: latestPrices,

        LatestPriceCount: latestPrices.length,

        /**
         * Spot/FWD averages from inventory rows.
         */
        SpotFixedAverage: spotFixedAvg,
        SpotDiffAverage: spotDiffAvg,
        SpotAverageDisplay: this.buildSlashPriceDisplay(spotDiffAvg, spotFixedAvg),

        ForwardFixedAverage: forwardFixedAvg,
        ForwardDiffAverage: forwardDiffAvg,
        ForwardAverageDisplay: this.buildSlashPriceDisplay(forwardDiffAvg, forwardFixedAvg),

        /**
         * RNY Overall Fixed/Diff Avg.
         * These are based on all inventory aged values, not spot/fwd split,
         * and not replacement values.
         */

        AverageLatestReplacementFixed: overallInventoryFixedAvg ?? 0,
        AverageLatestReplacementDiff: overallInventoryDiffAvg ?? 0,

        AverageLatestReplacementFixedDisplay: this.formatCurrencyDisplay(overallInventoryFixedAvg),
        AverageLatestReplacementDiffDisplay: this.formatCPriceDisplay(overallInventoryDiffAvg),


        /**
         * Latest/known pricing JSON fields.
         * These should not be averaged because they should be the same per SKU.
         */

        AverageLatestAskFixed: latestAskFixed ?? 0,
        AverageLatestAskDiff: latestAskDiff ?? 0,

        AverageLatestAskFixedDisplay: this.formatCurrencyDisplay(latestAskFixed),
        AverageLatestAskDiffDisplay: this.formatCPriceDisplay(latestAskDiff),



        AverageLatestMarketPrice: latestMarketPrice ?? 0,
        AverageLatestMarketPriceDisplay: this.formatCurrencyDisplay(latestMarketPrice),



        AverageLatestForwardAskFixed: latestForwardAskFixed ?? 0,
        AverageLatestForwardAskDiff: latestForwardAskDiff ?? 0,

        AverageLatestForwardAskFixedDisplay: this.formatCurrencyDisplay(latestForwardAskFixed),
        AverageLatestForwardAskDiffDisplay: this.formatCPriceDisplay(latestForwardAskDiff),


        SuggestedFixedPrice: suggestedFixedPrice ?? 0,

        SuggestedDiffPrice: suggestedDiffPrice ?? 0,

        SuggestedPriceDisplay: suggestedPriceDisplay,

        /**
         * Replace values come from latest/known pricing JSON.
         */
        AverageHistoricalAskFixed: null,

        AverageHistoricalReplacementFixed: latestReplacementFixed ?? 0,
        AverageHistoricalReplacementDiff: latestReplacementDiff ?? 0,

        AverageHistoricalReplacementFixedDisplay: this.formatCurrencyDisplay(latestReplacementFixed),
        AverageHistoricalReplacementDiffDisplay: this.formatCPriceDisplay(latestReplacementDiff),


        OverallFixedComparableAvg: overallFixedComparableAvg,
        OverallDiffComparableAvg: overallDiffComparableAvg,
        ReplaceFixedComparableAvg: replaceFixedComparableAvg,
        ReplaceDiffComparableAvg: replaceDiffComparableAvg,

        WorstCaseColumnKey: worstCase?.key ?? null,
        WorstCaseComparableValue: worstCase?.comparableValue ?? null,

        RnyOverallFixedAvgClass:
          worstCase?.key === 'AverageLatestReplacementFixed' ? 'worst-case-cell' : '',

        RnyOverallDiffAvgClass:
          worstCase?.key === 'AverageLatestReplacementDiff' ? 'worst-case-cell' : '',

        ReplaceFixedClass:
          worstCase?.key === 'AverageHistoricalReplacementFixed' ? 'worst-case-cell' : '',

        ReplaceDiffClass:
          worstCase?.key === 'AverageHistoricalReplacementDiff' ? 'worst-case-cell' : '',

        MinLatestAskFixed: this.min(latestAskFixedValues),
        MaxLatestAskFixed: this.max(latestAskFixedValues),

        MinLatestReplacementFixed: this.min(latestReplacementFixedValues),
        MaxLatestReplacementFixed: this.max(latestReplacementFixedValues),

        AllAskPriceFixed: latestAskFixedValues,

        HasPricing: latestPrices.length > 0,

        InventoryTypePriceNeedsUpdate: needsUpdate,
        RowClass: needsUpdate ? 'red-highlight' : ''
      };
    });
    /**
     * Keep all SKUs in one table, grouped visually by group name.
     */
    this.filteredSKUs = this.filteredSKUs
      .filter((f: any) => !String(f.InventoryTypeName || '').includes('*'))
      .sort((a, b) => {
        return String(a.InventoryTypeName || a.SKU || '')
          .localeCompare(String(b.InventoryTypeName || b.SKU || ''));
      });

    this.buildSummary();


  }

  setAllSelected(selected: boolean) {
    const visible = new Set((this.pricingTable?.filteredData ?? this.filteredSKUs).map(row => row.InventoryTypeID));
    this.filteredSKUs = this.filteredSKUs.map(row => ({...row, Selected: selected ? visible.has(row.InventoryTypeID) || row.Selected : false}));
  }
  ShowSelectedSkuInventory() { this.openInventoryDetails(this.filteredSKUs.filter(s=>s.Selected), 'all'); }
  ShowAllSkuInventory(row:any, section:'all'|'spot'|'forward'='all') { this.openInventoryDetails([row],section); }
  private openInventoryDetails(skus:any[], section:'all'|'spot'|'forward') {
    if(!skus.length) return;
    const inventory = Array.from(new Map(skus.flatMap(s=>s.Inventory).map(r=>[r.InventoryID,r])).values());
    const ref=this.pulldownService.open(InventoryListComponent, {
      data:{inventory,section,skus:skus.map(s=>({...s,pricing:{CurrentMarketPrice:this.getBasisForFuturesCompositeID(s.FuturesCompositeID) ?? this.CurrentNYCBasis}}))},
      title:skus.length===1?'Inventory Details for '+skus[0].InventoryTypeName:`Inventory Details · ${skus.length} SKUs`,
      movable:true,width:'98vw',maxWidth:'98vw',center:false,panelClass:['sku-pricing-top-pane','sku-inventory-expanded-pane'],top:'0',minHeight:'94dvh',maxHeight:'100dvh'
    });
    let pricesChanged=false;
    const subscription=ref.returnData().subscribe((data:any)=>{
      if(data?.success) pricesChanged=true;
      if(!data?.excluded) return;
      for(const item of inventory) item.PricingExcluded=data.excluded.includes(item.InventoryID);
      this.buildFilteredSKUs();
    });
    ref.afterClosed().subscribe(async()=>{subscription.unsubscribe();if(pricesChanged) await this.ngOnInit();});
  }
  ShowForwardSkuInventory(row: any) { this.ShowAllSkuInventory(row, 'forward'); }
  ShowSpotSkuInventory(row: any) { this.ShowAllSkuInventory(row, 'spot'); }
  private buildColumns() {
    this.skuPricingColumns = [
      this.column({
        label: 'Selected',
        key: 'Selected',
        type: 'checkbox',
        SearchType: 'checkbox',
        checkAll: true,
        edit: true,
        width: 4
      }),
      this.column({
        label: 'Sku Name',
        key: 'InventoryTypeName',
        width: 19,
        clickable: true,
        onAction: (row: any) => {
          this.ShowAllSkuInventory(row);
        }
      }),
      this.column({
        label: 'Spot Bags Available',
        key: 'TotalAvailableQty',
        width:5
      }),
      this.column({
        label: 'Spot Cost',
        key: 'SpotCostDetail',
        clickable: true,
        onAction: (row: any) => {
          this.ShowSpotSkuInventory(row);
        },
        width: 5
      }),
      this.column({
        label: 'FWD Bags Available',
        key: 'TotalForwardQty',
        width: 5
      }),
      this.column({
        label: 'FWD Cost',
        key: 'ForwardCostDetail',
        clickable: true,
        onAction: (row: any) => {
          this.ShowForwardSkuInventory(row);
        },
        width: 5
      }),
      this.column({
        label: 'Overall Fixed Avg',
        key: 'AverageLatestReplacementFixedDisplay',
        cellClass: (row: any) => row.RnyOverallFixedAvgClass,
        width: 8
      }),
      this.column({
        label: 'Overall Diff Avg',
        key: 'AverageLatestReplacementDiffDisplay',
        cellClass: (row: any) => row.RnyOverallDiffAvgClass,
        width: 8
      }),
      this.column({
        label: 'Replace Fixed',
        key: 'AverageHistoricalReplacementFixedDisplay',
        cellClass: (row: any) => row.ReplaceFixedClass,
        width: 8
      }),
      this.column({
        label: 'Replace Diff',
        key: 'AverageHistoricalReplacementDiffDisplay',
        cellClass: (row: any) => row.ReplaceDiffClass,
        width: 8
      }),
      this.column({
        label: 'Ask Fixed',
        key: 'AverageLatestAskFixedDisplay',
        width: 5
      }),
      this.column({
        label: 'Ask Diff',
        key: 'AverageLatestAskDiffDisplay',
        width: 5
      }),
      this.column({
        label: "'C' Price",
        key: 'AverageLatestMarketPriceDisplay',
        width: 5
      }),
      this.column({
        label: 'Suggested Price',
        key: 'SuggestedPriceDisplay',
        width: 5
      }),

    ];
  }

  private applyInventoryFilters(rows: InventoryDetailRow[]) {
    let filtered = rows || [];

    const selectedSingleGroupId =
      this.CustomGroupFilter === 'Single' && this.GroupID !== null
        ? Number(this.GroupID)
        : null;

    const selectedCustomGroupIds =
      this.CustomGroupFilter === 'Custom'
        ? this.SelectedGroups
          .map(x => Number(x))
          .filter(x => Number.isFinite(x))
        : [];

    const hasSingleGroupFilter =
      selectedSingleGroupId !== null &&
      Number.isFinite(selectedSingleGroupId);

    const hasCustomGroupFilter =
      selectedCustomGroupIds.length > 0;

    /**
     * No actual group/custom filter selected:
     * show only inventory that needs pricing updates.
     */
    if (this.needsUpdateOnly) {
      const pendingSKUs = new Set(filtered.filter(x => x.InventoryTypePriceNeedsUpdate === true || x.InventoryTypePriceNeedsUpdate === 1).map(x => x.InventoryTypeID));
      filtered = filtered.filter(x => pendingSKUs.has(x.InventoryTypeID));
    }

    if (hasSingleGroupFilter) {
      filtered = filtered.filter(x =>
        Number(x.GroupID ?? x.CoffeeSKUGroupID) === selectedSingleGroupId
      );
    }

    if (hasCustomGroupFilter) {
      const selectedSet = new Set(selectedCustomGroupIds);

      filtered = filtered.filter(x =>
        selectedSet.has(Number(x.GroupID ?? x.CoffeeSKUGroupID))
      );
    }

    if (this.RegionID !== null) filtered = filtered.filter(r => Number(r.RegionID) === Number(this.RegionID));
    if (this.CountryID !== null) filtered = filtered.filter(r => Number(r.CountryID) === Number(this.CountryID));
    if (this.FuturesMonth) filtered = filtered.filter((r: any) => r.FutureCompositeName === this.FuturesMonth);
    if(this.DashboardInventoryTypeID) filtered=filtered.filter(r=>r.InventoryTypeID===this.DashboardInventoryTypeID);
    const term = this.searchTerm?.trim().toLowerCase();

    if (term) {
      filtered = filtered.filter(x => {
        const searchable = [
          x.GroupName,
          x.InventoryTypeName,
          x.CoffeeSKUGroupID,
          x.InventoryTypeID
        ]
          .filter(v => v !== null && v !== undefined && v !== '')
          .join(' ')
          .toLowerCase();

        return searchable.includes(term);
      });
    }

    return filtered;
  }

  private getPricedItems(inventory: InventoryDetailRow[]): PricedItem[] {
    return inventory.flatMap(item => {
      const latestPrices = this.parseJsonArray(item.LatestInventoryTypePriceJson);
      const historyPrices = this.parseJsonArray(item.InventoryTypePriceHistoryJson);

      return [
        ...latestPrices.map(price => ({
          item,
          price,
          source: 'latest' as const
        })),
        ...historyPrices.map(price => ({
          item,
          price,
          source: 'history' as const
        }))
      ];
    });
  }

  private parseJsonArray(value: any): PricingRow[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'object') {
      return [value];
    }

    if (typeof value !== 'string') {
      return [];
    }

    const trimmed = value.trim();

    if (!trimmed) {
      return [];
    }

    const cached = this.jsonParseCache.get(trimmed);

    if (cached) {
      return cached;
    }

    try {
      const parsed = JSON.parse(trimmed);

      let result: PricingRow[];

      if (Array.isArray(parsed)) {
        result = parsed;
      } else if (parsed && typeof parsed === 'object') {
        result = [parsed];
      } else {
        result = [];
      }

      this.jsonParseCache.set(trimmed, result);
      return result;
    } catch {
      this.jsonParseCache.set(trimmed, []);
      return [];
    }
  }

  private getInventoryTypeKey(row: InventoryDetailRow): string {
    return String(
      row.InventoryTypeID ??
      row.CoffeeSKUGroupID ??
      row.GroupID ??
      ''
    ).trim();
  }

  private getNumberValues(
    rows: PricingRow[],
    key: keyof PricingRow
  ): number[] {
    return (rows || [])
      .map(row => this.toNumberOrNull(row?.[key]))
      .filter((v): v is number => v !== null);
  }

  private getBasisForFuturesCompositeID(futuresCompositeID: any): number | null {
    const compositeId = this.toNumberOrNull(futuresCompositeID);

    if (compositeId === null) {
      return null;
    }

    const nycCompositeId = this.toNumberOrNull(this.CurrentNYCBasisRow?.FuturesCompositeID);
    const ldnCompositeId = this.toNumberOrNull(this.CurrentLDNBasisRow?.FuturesCompositeID);

    if (
      nycCompositeId !== null &&
      compositeId === nycCompositeId &&
      this.CurrentNYCBasis !== null
    ) {
      return this.CurrentNYCBasis;
    }

    if (
      ldnCompositeId !== null &&
      compositeId === ldnCompositeId &&
      this.CurrentLDNBasis !== null
    ) {
      return this.CurrentLDNBasis;
    }

    return null;
  }

  private diffToComparableValue(
    rawDiff: any,
    inventoryRow: InventoryDetailRow,
    pricingRow?: PricingRow
  ): number | null {
    const diff = this.toNumberOrNull(rawDiff);

    if (diff === null) {
      return null;
    }

    const futuresCompositeID =
      inventoryRow?.FuturesCompositeID ??
      pricingRow?.FuturesCompositeID ??
      null;

    const matchedBasis = this.getBasisForFuturesCompositeID(futuresCompositeID);

    const basis =
      matchedBasis ??
      this.CurrentNYCBasis;

    if (basis === null) {
      return null;
    }
    return Number((basis + diff).toFixed(4));
  }

  private getWorstCaseColumn(
    candidates: Array<{
      key: string;
      comparableValue: number | null;
    }>
  ): {
    key: string;
    comparableValue: number;
  } | null {
    const valid = candidates
      .filter((x): x is { key: string; comparableValue: number } =>
        x.comparableValue !== null &&
        Number.isFinite(x.comparableValue)
      );

    if (!valid.length) {
      return null;
    }

    return valid.reduce((highest, current) =>
      current.comparableValue > highest.comparableValue
        ? current
        : highest
    );
  }

  private firstNumberOrNull(values: any[]): number | null {
    for (const value of values) {
      const numberValue = this.toNumberOrNull(value);

      if (numberValue !== null) {
        return numberValue;
      }
    }

    return null;
  }

  private toNumberOrNull(raw: any): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }

    const value = Number(raw);

    return Number.isFinite(value) ? value : null;
  }

  private numberOrZero(raw: any): number {
    const value = this.toNumberOrNull(raw);
    return value ?? 0;
  }

  private average(values: number[]): number | null {
    if (!values.length) {
      return null;
    }

    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(4));
  }

  private min(values: number[]): number | null {
    return values.length ? Math.min(...values) : null;
  }

  private max(values: number[]): number | null {
    return values.length ? Math.max(...values) : null;
  }

  private column(config: any): Column {
    return config as Column;
  }

  private buildSummary() {
    const rows = this.filteredSKUs;
    const inventory = rows.flatMap(row => row.Inventory || []);
    const spot = summarizeCosts(inventory, 'AvailableQty', 'aged');
    const forward = summarizeCosts(inventory, 'ForwardQty', 'landed');
    this.Summary = {
      SKUCount: rows.length,
      TotalSpotBags: spot.bags,
      TotalForwardBags: forward.bags,
      AverageSpotDisplay: spot.detail,
      AverageForwardDisplay: forward.detail,
      AverageReplacementFixed: this.average(rows.map(r => this.toNumberOrNull(r.LatestSavedPricing?.ReplacementFixed)).filter((n): n is number => n !== null)),
      AverageMarketPrice: this.average(rows.map(r => this.toNumberOrNull(r.LatestSavedPricing?.CurrentMarketPrice)).filter((n): n is number => n !== null))
    };
  }
  private getInventoryAgedCost(row: InventoryDetailRow): number | null {
    return this.toNumberOrNull(
      (row as any).agedwhseincost ??
      (row as any).AgedWhseInCost ??
      (row as any).AgedWhseInCostAvg ??
      null
    );
  }

  private getInventoryAgedDiff(row: InventoryDetailRow): number | null {
    return this.toNumberOrNull(
      (row as any).agedwhseindif ??
      (row as any).AgedWhseInDif ??
      (row as any).AgedWhseInDiff ??
      (row as any).AgedWhseInDiffAvg ??
      null
    );
  }

  private formatDiff(value: number | null): string | null {
    if (value === null || value <= 0) {
      return null;
    }

    return value
      .toFixed(2)
      .replace(/^0\./, '.')
      .replace(/^-0\./, '-.');
  }

  private buildSlashPriceDisplay(
    diffAvg: number | null,
    fixedAvg: number | null
  ): string {
    return priceDisplay(diffAvg, fixedAvg);
  }
  private hasUsableAgedFixed(row: InventoryDetailRow): boolean {
    const fixed = this.getInventoryAgedCost(row);
    return fixed !== null && fixed > 0;
  }

  private getUsableAgedFixed(row: InventoryDetailRow): number | null {
    return (row as any).PricingExcluded ? null : costValue(row, 'aged', false);
  }

  private getUsableAgedDiff(row: InventoryDetailRow): number | null {
    return (row as any).PricingExcluded ? null : costValue(row, 'aged', true);
  }
  private firstPriceNumber(
    rows: PricingRow[],
    key: keyof PricingRow
  ): number | null {
    for (const row of rows || []) {
      const value = this.toNumberOrNull(row?.[key]);

      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  private formatDiffAllowZero(value: number | null): string {
    const safeValue = this.toNumberOrNull(value) ?? 0;

    return safeValue
      .toFixed(2)
      .replace(/^0\./, '.')
      .replace(/^-0\./, '-.');
  }

  private formatCPriceDisplay(value: number | null): string {
    return priceDisplay(this.toNumberOrNull(value), null);
  }
  private formatCurrencyDisplay(value: number | null): string {
    return priceDisplay(null, this.toNumberOrNull(value));
  }
  private getSuggestedPrice(
    cost: number | null,
    type: 'Fixed' | 'Diff',
    priceBase?: number | null
  ): number | null {

    if (cost === null || (type === 'Fixed' && cost <= 0)) {
      return null;
    }

    const expectedMargin = 0.90;
    const expectedMarginLimit = 0.35;

    let suggested: number;

    if (type === 'Fixed') {
      suggested = cost / expectedMargin;
    } else {

      const basis = priceBase ?? this.CurrentNYCBasis;
      if (basis === null) return null;

      suggested =
        ((cost + basis) / expectedMargin) -
        basis;
    }

    if ((suggested - cost) < expectedMarginLimit) {
      suggested = cost + expectedMarginLimit;
    }

    return Math.floor(suggested * 100) / 100;
  }
}
